import {Dispatch, useCallback} from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {
    deleteAutomationRule,
    fetchAlertChooses,
    fetchAutomationRulesList,
    saveAutomationRule,
    setAutomationRuleOrder
} from '../../../actions';
import {ActionTypes, AutomationAction} from '../../../reducer/actions';
import {formAlertReport, isNewApiForm} from '../../../../../_shared/GWTBridge/invoke/netForm';
import {AutomationContextType, AutomationFilter} from '../../../reducer/model';
import {Form} from '../../../../../stores/Form/Form';

import {APIActionStatus, formsStore} from '../../../../../stores/Form';
import {
    prepareChooses,
    prepareListData
} from '../../../components/RulesListDialog/parts/AutomationsOverview/utils';
import {RuleType, AutomationTypeNames} from '../../../enums';
import {callLinkedActions} from '../../../../Form/actions/screen';
import {lockScreenKeyboardNavigation} from '../../../../Form/actions/lockScreenKeyboardNavigation';
import {AlertListItem} from '../../../reducer/types';
import {returnFocus} from '../../../../../_shared/GWTBridge/invoke/genPage';
import {closeBridgeForms, runBridgeForm} from '../../../actions/bridgeActions/actions';
import {getFormName} from '../../../../../_shared/GWTBridge/invoke/recon';
import {fetchSysLangs} from '../../../../../Localization/languages';
import {noop} from '@priority-software/priority-style-react';
import {sendMonitor} from '../../../../../utils/monitoring';
import {useAutomationCommonHandlers} from '../../../hooks/useAutomationCommonHandlers';
import {
    BusinessRuleListActiveToggle,
    BusinessRuleListInActiveToggle
} from '../../../components/RulesListDialog/consts';
import {monitorAction} from '../../../../../services/datadog';
import {AIRulesMonitoring} from '../../../components/common/AIAskBlock/enums';
import {getRuleKeyByValue} from '../../../utils';

export type AutomationContextHandlers = {
    handleAutomationOpen: () => void;
    handleDialogType: (type: RuleType) => void;
    openRuleDialog: () => void;
    executeFormAlertReport: (type: RuleType, callback: () => void) => void;
    closeSingleRuleDialog: () => void;
    openAutomationList: (params: {
        showInPopup?: boolean;
        type: RuleType;
        form: Form;
    }) => Promise<void>;
    removeRule: (ruleNumber: number) => Promise<void>;
    editRule: (ruleNumber: number, fetchHTML?: boolean) => void;
    copyRule: (ruleNumber: number) => Promise<void>;
    handleRuleActive: (
        ruleNumber: number,
        isActive: boolean,
        callback: () => void
    ) => Promise<void>;
    setFilter: (filter: AutomationFilter) => void;
    setRules: (rules: Array<AlertListItem>) => void;
    removeFilter: (filterName: string, value: string | number | boolean) => void;
    closeAutomation: () => void;
    handleSearch: (searchValue: string) => void;
    handleRuleOrder: (rules: Array<AlertListItem>) => void;
    saveRule: (
        rule: priority.netitems.form.IAlert,
        type: priority.netitems.form.RuleType,
        onSuccess?: () => void
    ) => Promise<void>;
    updateOpenRule: (data: Partial<priority.netitems.form.IAlert>) => void;
    handleFilters: () => void;
    closeCurrentAutomation: () => void;
    setSelectedRuleIndex: (index: number) => void;
    handleAIRuleSuggestions: (data: {
        openRule: priority.netitems.form.IAlert;
        aiGeneratedRuleQuery: string;
    }) => void;
    getEmailTemplateFields: () => Promise<void>;
};

export const useAutomationContextAPI = (
    dispatch: Dispatch<AutomationAction>,
    state: AutomationContextType,
    form: Form
) => {
    const {useBridgeAPI, listResponse, automationType, aiGeneratedRuleQuery} = state;
    const session = form?.session;
    const hasAIQuery = !!aiGeneratedRuleQuery;

    const {
        getFullRuleInfo,
        setRules,
        setSelectedRuleIndex,
        setFilter,
        closeSingleRuleDialog,
        openRuleDialog,
        updateOpenRule,
        handleFilters,
        handleSearch,
        removeFilter,
        handleDialogType,
        handleAIRuleSuggestions,
        handleAutomationOpen: openAutomation,
        getEmailTemplateFields
    } = useAutomationCommonHandlers(dispatch, state);

    const closeCurrentAutomation = () => {
        lockScreenKeyboardNavigation(false);
        dispatch({type: ActionTypes.CLOSE_AUTOMATION});
    };

    const closeAutomation = useCallback(async () => {
        dispatch({type: ActionTypes.CLOSE_AUTOMATION});

        if (useBridgeAPI) {
            await closeBridgeForms(session);
            // return focus to the old ui
            requestAnimationFrame(() => returnFocus());
        } else {
            // return focus to the new UI
            if (form?.formActiveElement) {
                requestAnimationFrame(() => {
                    form.formActiveElement.focus();
                });
            }
        }

        lockScreenKeyboardNavigation(false);
    }, [session, useBridgeAPI]);

    const openAutomationList = useCallback(
        async ({showInPopup, type}) => {
            if (!session) {
                await runBridgeForm();
            }
            const currentScreen = formsStore.currentScreen;
            if (!currentScreen) return;

            const formSession = session || currentScreen.session;

            // Set the automation type immediately to show the dialog with loading state
            dispatch({type: ActionTypes.SET_AUTOMATION_TYPE, payload: {type}});
            lockScreenKeyboardNavigation(true);

            await callLinkedActions(
                async () => {
                    if (currentScreen) {
                        // If user add some data in the grid we should save or revert it before calling automations
                        return currentScreen.checkAndSave();
                    }
                    return APIActionStatus.passed;
                },
                async () => {
                    if (showInPopup) {
                        if (type === RuleType.business || type === RuleType.autofill) {
                            const syslangs = await fetchSysLangs(true);
                            const chooses = await fetchAlertChooses(formSession);
                            const choosesOptions = prepareChooses(chooses, syslangs);

                            dispatch({
                                type: ActionTypes.SET_AUTOMATION_CHOOSES,
                                payload: {choosesOptions}
                            });

                            const res = await fetchAutomationRulesList(formSession, type);

                            const rules = prepareListData(res, choosesOptions, type);
                            dispatch({
                                type: ActionTypes.UPDATE_LIST_RESPONSE,
                                payload: {data: res}
                            });

                            const formName = currentScreen?.getActiveForm()?.ename || getFormName();
                            dispatch({type: ActionTypes.DISPATCH_STATE, payload: {formName}});
                            setRules(rules);
                        }
                    }

                    // Open the automation after data is loaded
                    dispatch({type: ActionTypes.OPEN_AUTOMATION});
                    return APIActionStatus.passed;
                }
            );
        },
        [form, setRules, session]
    );

    const removeRule = useCallback(
        async (ruleNumber: number) => {
            const isOk = await deleteAutomationRule(
                session,
                ruleNumber,
                closeCurrentAutomation,
                useBridgeAPI
            );

            if (isOk) {
                dispatch({type: ActionTypes.REMOVE_RULE, payload: {ruleNumber}});
                dispatch({type: ActionTypes.HANDLE_RULE_FILTERS});
            }
        },
        [session]
    );

    const editRule = useCallback(
        async (ruleNumber: number, fetchHtml?: boolean) => openRuleDialog(ruleNumber, fetchHtml),
        [updateOpenRule]
    );

    const saveRule = useCallback(
        async (
            rule: priority.netitems.form.IAlert,
            type: priority.netitems.form.RuleType,
            onSuccess?: () => void
        ) => {
            const rules = await saveAutomationRule(
                rule,
                type,
                session,
                closeCurrentAutomation,
                useBridgeAPI
            );

            if (hasAIQuery) {
                monitorAction(AIRulesMonitoring.aiRuleSave, {
                    ruleType: getRuleKeyByValue(automationType),
                    query: aiGeneratedRuleQuery
                });
            }
            if (rules.length > 0) {
                dispatch({type: ActionTypes.SAVE_RULE, payload: {rules}});
                dispatch({type: ActionTypes.HANDLE_RULE_FILTERS});
                onSuccess?.();
            }
        },
        [session, useBridgeAPI, hasAIQuery]
    );

    const copyRule = useCallback(
        async (copyRuleNumber: number) => {
            try {
                // Set loading state to true before starting the copy operation
                dispatch({type: ActionTypes.SET_COPYING_STATE, payload: {isCopying: true}});

                const rule = listResponse.find(rule => rule.AlertNumber === copyRuleNumber);
                if (rule) {
                    const newRule = await getFullRuleInfo(rule);
                    newRule.AlertNumber = null; // remove alertNumber to create a new copy of the rule

                    await saveRule(newRule, automationType as number);
                    dispatch({type: ActionTypes.HANDLE_RULE_FILTERS});
                }
            } finally {
                // Set loading state to false after the copy operation is complete
                dispatch({type: ActionTypes.SET_COPYING_STATE, payload: {isCopying: false}});
            }
        },
        [session, listResponse, automationType, saveRule, dispatch]
    );

    const handleRuleActive = useCallback(
        async (ruleNumber: number, isActive: boolean, callback: () => void) => {
            const rule = listResponse.find(item => item.AlertNumber === ruleNumber);

            if (rule) {
                const updatedRule = {
                    ...(await getFullRuleInfo(rule)),
                    Active: isActive
                };

                if (automationType === RuleType.business) {
                    sendMonitor(
                        isActive ? BusinessRuleListActiveToggle : BusinessRuleListInActiveToggle
                    );
                }

                const rules = await saveAutomationRule(
                    updatedRule,
                    automationType as number,
                    session,
                    closeCurrentAutomation,
                    useBridgeAPI
                );
                dispatch({
                    type: ActionTypes.SET_ACTIVE_STATE,
                    payload: {ruleNumber, isActive, rules}
                });
                callback();
            }
        },
        [listResponse, session, automationType, useBridgeAPI]
    );

    const handleRuleOrder = useCallback(async (orderedRules: AlertListItem[]) => {
        dispatch({type: ActionTypes.SET_AUTOMATION_RULES, payload: {rules: orderedRules}});
        handleSearch(); // reset filtered array
        const updatedAlertOrder = orderedRules.map((item, Line) => ({
            Alert: item.alertNumber,
            Line
        }));
        await setAutomationRuleOrder(updatedAlertOrder);
    }, []);

    const executeFormAlertReport = useCallback(async (type: RuleType, callback = noop) => {
        if (isNewApiForm(getFormName())) {
            // TODO
        } else {
            await formAlertReport(
                type === RuleType.business
                    ? AutomationTypeNames.Business
                    : AutomationTypeNames.AutoFill,
                callback
            );
        }
    }, []);

    const handleAutomationOpen = useCallback(() => {
        lockScreenKeyboardNavigation(true);

        openAutomation();
    }, [openAutomation, session]);

    return {
        handleAutomationOpen,
        handleDialogType,
        openRuleDialog,
        executeFormAlertReport,
        closeSingleRuleDialog,
        openAutomationList,
        removeRule,
        editRule,
        copyRule,
        handleRuleActive,
        setFilter,
        removeFilter,
        setRules,
        closeAutomation,
        handleSearch,
        handleRuleOrder,
        saveRule,
        updateOpenRule,
        handleFilters,
        setSelectedRuleIndex,
        handleAIRuleSuggestions,
        closeCurrentAutomation,
        getEmailTemplateFields
    } as AutomationContextHandlers;
};
